import { Injectable, inject, InjectionToken, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import type { Observable } from 'rxjs';
import { BehaviorSubject, Subject, combineLatest, take, filter, map, mergeMap } from 'rxjs';

import { I18nService } from '@core/i18n-legacy';
import { injectReportsMetrics } from '@core/reports-contracts';
import { ReportDashboardMetricOptions, ReportsTranslations } from '@core/reports-types';

import { ReportsFilterTranslationsService } from './filters-translations.service';
import { ReportFiltersRepo } from './report-filters.repo';
import { ReportsRefreshService } from './reports-refresh.service';

// singleton to share data between reports in different contexts: ie. job dashboards and analytics
@Injectable({ providedIn: 'root' })
export class ReportsTranslationsStateService {
  public translationsLoaded$ = new BehaviorSubject<boolean>(false);
  public groupTranslations: Record<string, string> = {};
  public filterGroupTranslations = signal<Record<string, string>>({});
  public filterGroupParameterized = signal<Record<string, boolean>>({});
}

export const REPORTS_I18N_SERVICE = new InjectionToken<I18nService>('reportsI18nService');

// providable service to have the possibility of injecting non-root level dependencies
@Injectable()
export class ReportsTranslationsService implements ReportsTranslations {
  public i18n =
    inject(REPORTS_I18N_SERVICE, {
      optional: true,
    }) ?? inject(I18nService);
  public filterGroupTranslations$: Observable<Record<string, string>>;
  private filtersTranslationsService = inject(ReportsFilterTranslationsService);
  private reportFiltersRepo = inject(ReportFiltersRepo);

  private metrics$ = injectReportsMetrics();
  private state = inject(ReportsTranslationsStateService);

  private fieldGroupFetchQueue$ = new Subject<string>();
  private pendingFieldGroupFetches = new Set<string>();

  constructor() {
    this.filterGroupTranslations$ = toObservable(this.state.filterGroupTranslations);
    inject(ReportsRefreshService)
      .onRevisit$.pipe(takeUntilDestroyed())
      .subscribe(() => this.pendingFieldGroupFetches.clear());
    this.loadTranslations();
    this.fieldGroupFetchQueue$
      .pipe(mergeMap(baseGroup => this.reportFiltersRepo.loadFields(baseGroup)))
      .subscribe(fields => {
        const updates = fields.reduce<Record<string, string>>(
          (acc, f) => ({ ...acc, [f.id]: f.name }),
          {},
        );
        this.state.filterGroupTranslations.update(prev => ({ ...prev, ...updates }));
      });
  }

  public get translationsLoaded$() {
    return this.state.translationsLoaded$.asObservable();
  }

  public translateGroup(groupName: string | null | undefined): string {
    if (!groupName) {
      return '';
    }
    const result =
      this.state.groupTranslations[groupName] || this.state.filterGroupTranslations()[groupName];
    if (!result) {
      this.triggerGroupFetchIfNeeded(groupName);
    }
    return result || groupName;
  }

  public translateFilterGroup(groupName: string | null | undefined): string {
    if (!groupName) {
      return '';
    }
    const cached = this.state.filterGroupTranslations()[groupName];
    if (!cached) {
      this.triggerGroupFetchIfNeeded(groupName);
    }
    return cached || groupName;
  }

  public isFilterGroupParameterized(id: string): boolean {
    return this.state.filterGroupParameterized()[id] ?? false;
  }

  public translateFilterSync(groupId: string, id: string): { group: string; label: string } {
    const label = this.filtersTranslationsService.translateFilterSync(groupId, id);
    const group = this.translateFilterGroup(groupId);

    if (!label) {
      return { label: id, group };
    }
    return {
      group,
      label,
    };
  }
  public translateFilter(
    groupId: string,
    id: string,
  ): Observable<{ group: string; label: string }> {
    return combineLatest([
      this.filtersTranslationsService.translateFilter(groupId, id),
      this.filterGroupTranslations$,
    ]).pipe(
      map(([label]) => ({
        label,
        group: this.translateFilterGroup(groupId),
      })),
    );
  }

  public addFilterTranslation(translation: { label: string; group: string; id: string }) {
    this.filtersTranslationsService.addFilterTranslation(translation);
  }

  public addFilterGroupTranslation(translation: { id: string; name: string }): void {
    this.state.filterGroupTranslations.update(prev => ({
      ...prev,
      [translation.id]: translation.name,
    }));
  }

  private triggerGroupFetchIfNeeded(groupName: string): void {
    if (!groupName.includes(':')) {
      return;
    }
    const baseGroup = groupName.split(':')[0];
    if (!this.pendingFieldGroupFetches.has(baseGroup)) {
      this.pendingFieldGroupFetches.add(baseGroup);
      this.fieldGroupFetchQueue$.next(baseGroup);
    }
  }

  private loadTranslations(): void {
    this.metrics$
      .pipe(
        filter(x => x.length > 0),
        take(1),
      )
      .subscribe(metrics => {
        this.setTranslations(metrics);
        this.state.translationsLoaded$.next(true);
      });
  }

  private setTranslations(metrics: ReportDashboardMetricOptions[]): void {
    metrics.forEach(metric => {
      this.setGroupTranslationsCallback(metric);
      this.setFilterGroupTranslationsCallback(metric);
    });
  }

  private setGroupTranslationsCallback(metric: ReportDashboardMetricOptions): void {
    const primaryGroups = [
      ...metric.availableGroups,
      ...(metric.datePoints ?? []).map(x => x.availableGroups),
    ].flat();

    const allGroups = primaryGroups.map(group => [group, ...(group.secondaryGroups ?? [])]).flat();

    allGroups.forEach(({ id, name }) => {
      this.state.groupTranslations[id] = name;
    });
  }

  private setFilterGroupTranslationsCallback(metric: ReportDashboardMetricOptions): void {
    const filterGroups = [
      ...metric.availableFilters,
      ...(metric.datePoints ?? []).map(x => x.availableFilters),
    ].flat();

    const nameUpdates: Record<string, string> = {};
    const parameterizedUpdates: Record<string, boolean> = {};
    filterGroups.forEach(({ id, name, parameterized }) => {
      nameUpdates[id] = name;
      parameterizedUpdates[id] = parameterized;
    });
    this.state.filterGroupTranslations.update(prev => ({ ...prev, ...nameUpdates }));
    this.state.filterGroupParameterized.update(prev => ({ ...prev, ...parameterizedUpdates }));
  }
}
